'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowLeft, Search, ChevronDown, HelpCircle, Wrench, Clock, Shield, Smartphone } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const FAQ_CATEGORIES = [
  { id: 'all', label: 'All Questions', icon: HelpCircle },
  { id: 'repair-process', label: 'Repair Process', icon: Wrench },
  { id: 'turnaround', label: 'Turnaround Times', icon: Clock },
  { id: 'warranty', label: 'Warranty & Returns', icon: Shield },
  { id: 'eligibility', label: 'Device Eligibility', icon: Smartphone },
];

const FAQ_ITEMS: FAQItem[] = [
  // Repair Process
  {
    id: 'rp-1',
    category: 'repair-process',
    question: 'How does the mail-in repair process work?',
    answer: 'It\'s simple: book your repair online, securely package your device, and post it to us. Once we receive it, we\'ll diagnose the issue, confirm the repair cost, and get to work. After the repair is complete, we\'ll ship your device back to you. The whole process is designed to be hassle-free — no need to visit a store.',
  },
  {
    id: 'rp-2',
    category: 'repair-process',
    question: 'Do I need to book before sending my device?',
    answer: 'Yes, we ask that you book through our website before sending your device. This helps us prepare for your repair, assign a job number, and ensures your device is expected when it arrives. Walk-in or unannounced mail-ins may experience delays.',
  },
  {
    id: 'rp-3',
    category: 'repair-process',
    question: 'What happens if my device has additional faults found during repair?',
    answer: 'If our technician discovers additional issues beyond what was originally booked, we\'ll contact you before proceeding. We\'ll explain what was found and provide a quote for any extra work. We never carry out unapproved repairs — you\'re always in control.',
  },
  {
    id: 'rp-4',
    category: 'repair-process',
    question: 'Do I need to back up my device before sending it in?',
    answer: 'Yes — we strongly recommend backing up all your data before sending your device. While most repairs don\'t affect your data, some situations (such as logic board repairs or severe damage) may require a factory reset. LP Refurbished is not responsible for data loss during a repair.',
  },
  {
    id: 'rp-5',
    category: 'repair-process',
    question: 'Do I need to disable Find My iPhone before sending my device?',
    answer: 'For most repairs, you don\'t need to disable Find My iPhone. However, for certain repairs — particularly those involving the logic board or activation lock — we may ask you to disable it. We\'ll let you know if this is required when you book.',
  },
  {
    id: 'rp-6',
    category: 'repair-process',
    question: 'What parts do you use for repairs?',
    answer: 'We use high-quality replacement parts that meet or exceed OEM specifications. For screen replacements, we offer options ranging from aftermarket to premium OEM-grade displays. We\'ll always be upfront about the parts used and their quality tier so you can make an informed decision.',
  },
  {
    id: 'rp-7',
    category: 'repair-process',
    question: 'How should I package my device for mail-in?',
    answer: 'Pack your device securely in a padded envelope or small box with bubble wrap or foam padding. Remove any cases or accessories unless they\'re relevant to the repair. Include a note with your name, contact number, and job reference number. We recommend using tracked postage for peace of mind.',
  },
  // Turnaround Times
  {
    id: 'tt-1',
    category: 'turnaround',
    question: 'How long does a typical repair take?',
    answer: 'Most standard repairs — such as screen replacements, battery replacements, and charging port repairs — are completed within a few hours. More complex repairs, such as water damage or logic board issues, may take longer. We\'ll give you an estimated timeframe when we confirm your repair.',
  },
  {
    id: 'tt-2',
    category: 'turnaround',
    question: 'How long does shipping take after my repair is complete?',
    answer: 'Once we receive your device, we aim to complete the repair and ship it back to you the same day. Return delivery typically takes 2–5 business days depending on your location within Australia. Express postage options may be available on request.',
  },
  {
    id: 'tt-3',
    category: 'turnaround',
    question: 'What is the total turnaround time for a mail-in repair?',
    answer: 'Once we receive your device, we aim to repair and ship it back the same day. Return delivery typically takes 2–5 business days depending on your location. Complex repairs may take a little longer, and we\'ll always keep you updated.',
  },
  {
    id: 'tt-4',
    category: 'turnaround',
    question: 'Will I be updated on the status of my repair?',
    answer: 'Yes. We\'ll notify you when your device is received, when the repair is underway, and when it\'s been shipped back. If there are any delays or additional issues found, we\'ll contact you promptly. You can also reach us directly by phone or email for an update at any time.',
  },
  {
    id: 'tt-5',
    category: 'turnaround',
    question: 'Can I get an express or same-day repair?',
    answer: 'For drop-off repairs, most standard repairs are completed within a few hours on the same day. For mail-in repairs, we aim to repair and return your device the same day we receive it. Contact us before booking if you have an urgent timeline and we\'ll do our best to help.',
  },
  // Warranty
  {
    id: 'wc-1',
    category: 'warranty',
    question: 'What warranty do you offer on repairs?',
    answer: 'Every repair completed by LP Refurbished is backed by a 3-month warranty on the replaced parts and workmanship. This covers defects in the parts we supplied, faults caused by our workmanship, and issues directly related to the repair performed.',
  },
  {
    id: 'wc-2',
    category: 'warranty',
    question: 'What is not covered by the repair warranty?',
    answer: 'The repair warranty does not cover accidental damage (drops, cracks, impacts), liquid damage after the repair, damage caused by another repairer, pre-existing faults unrelated to the repair, normal wear and tear, or damage from misuse or unsuitable accessories.',
  },
  {
    id: 'wc-3',
    category: 'warranty',
    question: 'Do refurbished devices come with a warranty?',
    answer: 'Yes. All refurbished devices sold by LP Refurbished come with a 3-month warranty against covered hardware faults. This includes the screen, battery, charging port, cameras, speakers, microphones, buttons, and connectivity hardware, subject to the warranty conditions.',
  },
  {
    id: 'wc-4',
    category: 'warranty',
    question: 'How do I make a warranty claim?',
    answer: 'Contact us through our website or the contact details on your invoice. Provide your order or repair reference number and a description of the issue. Where possible, include photos or a short video. We\'ll ask you to bring or send the device in for inspection, and once assessed, we\'ll advise on the next steps.',
  },
  {
    id: 'wc-5',
    category: 'warranty',
    question: 'Does a warranty repair extend my original warranty period?',
    answer: 'No. A warranty repair or replacement does not automatically restart or extend the original 3-month warranty period. The warranty runs from the original repair completion or purchase date.',
  },
  {
    id: 'wc-6',
    category: 'warranty',
    question: 'Do you offer change-of-mind returns?',
    answer: 'LP Refurbished does not offer change-of-mind returns on completed repairs, custom orders, or opened accessories. For refurbished devices, change-of-mind returns are not offered unless otherwise required by law. Please check the device details carefully before purchasing.',
  },
  {
    id: 'wc-7',
    category: 'warranty',
    question: 'Are your repairs covered by Australian Consumer Law?',
    answer: 'Yes. Our goods and services come with guarantees that cannot be excluded under the Australian Consumer Law (ACL). Our warranty policy is provided in addition to your ACL rights — nothing in our policy removes or limits any rights that cannot legally be excluded.',
  },
  // Device Eligibility
  {
    id: 'de-1',
    category: 'eligibility',
    question: 'What devices do you repair?',
    answer: 'We primarily repair smartphones, with a focus on iPhones and popular Android devices. This includes screen replacements, battery replacements, charging port repairs, camera repairs, and more. If you\'re unsure whether we can repair your specific device, contact us or use the repair booking tool to check.',
  },
  {
    id: 'de-2',
    category: 'eligibility',
    question: 'Do you repair water-damaged devices?',
    answer: 'Yes, we accept water-damaged devices for repair on a best-effort basis. Liquid damage can affect multiple components and outcomes cannot be guaranteed. We\'ll assess the device and discuss the likely outcome and any warranty limitations before proceeding. Any warranty for liquid-damaged repairs will be specifically agreed upon beforehand.',
  },
  {
    id: 'de-3',
    category: 'eligibility',
    question: 'Can you repair a device that won\'t turn on?',
    answer: 'Yes, we can attempt to diagnose and repair devices that won\'t power on. Note that if a device couldn\'t be fully tested before the repair due to being non-functional, the warranty may be limited to the specific repair performed and directly related workmanship.',
  },
  {
    id: 'de-4',
    category: 'eligibility',
    question: 'Do you repair devices with a cracked back glass?',
    answer: 'Yes, back glass replacement is available for many iPhone and Android models. The cost and complexity varies by model. Use our repair booking tool to check pricing for your specific device.',
  },
  {
    id: 'de-5',
    category: 'eligibility',
    question: 'Can you repair a device that has been repaired elsewhere before?',
    answer: 'Yes, we can attempt to repair devices that have been previously repaired by another technician. However, if prior repairs have caused additional damage or used non-standard parts, this may affect the outcome and warranty coverage. We\'ll assess the device and be upfront about any complications.',
  },
  {
    id: 'de-6',
    category: 'eligibility',
    question: 'What refurbished devices do you sell?',
    answer: 'We sell refurbished smartphones that have been tested, cleaned, and graded for quality. Our stock changes regularly — check the Refurbished Phones page for current availability. All devices come with a 3-month warranty against covered hardware faults.',
  },
];

function AccordionItem({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-200"
      style={{
        border: open ? '1px solid rgba(212,168,67,0.3)' : '1px solid #2a2a2a',
        backgroundColor: open ? 'rgba(212,168,67,0.04)' : '#111111',
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-bold leading-snug" style={{ color: '#f5f0e8' }}>{item.question}</span>
        <ChevronDown
          size={18}
          className={`flex-shrink-0 mt-0.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          style={{ color: open ? '#d4a843' : '#5a4a35' }}
        />
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-sm leading-relaxed" style={{ color: '#8a7a65' }}>{item.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = useMemo(() => {
    let items = FAQ_ITEMS;
    if (activeCategory !== 'all') {
      items = items.filter((i) => i.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (i) =>
          i.question.toLowerCase().includes(q) ||
          i.answer.toLowerCase().includes(q)
      );
    }
    return items;
  }, [search, activeCategory]);

  const groupedByCategory = useMemo(() => {
    if (activeCategory !== 'all') {
      return { [activeCategory]: filtered };
    }
    return filtered.reduce<Record<string, FAQItem[]>>((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  }, [filtered, activeCategory]);

  const categoryLabels: Record<string, string> = {
    'repair-process': 'Repair Process',
    turnaround: 'Turnaround Times',
    warranty: 'Warranty & Returns',
    eligibility: 'Device Eligibility',
  };

  const hasResults = filtered.length > 0;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0a0a0a' }}>
      <Header />
      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="border-b py-14 px-6" style={{ background: 'linear-gradient(135deg, #0d0d0d 0%, #111111 50%, #0a0a0a 100%)', borderColor: '#2a2a2a' }}>
          <div className="max-w-4xl mx-auto">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-sm font-semibold transition-colors mb-6"
              style={{ color: '#8a7a65' }}
            >
              <ArrowLeft size={15} />
              Back to Home
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(212,168,67,0.12)' }}>
                <HelpCircle size={24} style={{ color: '#d4a843' }} />
              </div>
              <span className="section-label">Help Centre</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3" style={{ color: '#f5f0e8' }}>
              Frequently Asked Questions
            </h1>
            <p className="text-base leading-relaxed max-w-2xl mb-8" style={{ color: '#8a7a65' }}>
              Find answers to common questions about our repair process, turnaround times, warranty coverage, and device eligibility.
            </p>

            {/* Search */}
            <div className="relative max-w-xl">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#5a4a35' }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search questions..."
                className="w-full pl-11 pr-5 py-3.5 rounded-2xl text-sm font-medium transition-all shadow-sm"
                style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a', color: '#f5f0e8', outline: 'none' }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold transition-colors"
                  style={{ color: '#5a4a35' }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Category Tabs + Content */}
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto">
            {/* Category pills */}
            {!search && (
              <div className="flex flex-wrap gap-2 mb-10">
                {FAQ_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = activeCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-150"
                      style={{
                        backgroundColor: isActive ? '#d4a843' : '#1a1a1a',
                        color: isActive ? '#0a0a0a' : '#8a7a65',
                        border: isActive ? 'none' : '1px solid #2a2a2a',
                      }}
                    >
                      <Icon size={14} />
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Search result count */}
            {search && (
              <p className="text-sm mb-6 font-medium" style={{ color: '#8a7a65' }}>
                {filtered.length === 0
                  ? 'No results found'
                  : `${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${search}"`}
              </p>
            )}

            {/* FAQ items */}
            {hasResults ? (
              <div className="flex flex-col gap-10">
                {Object.entries(groupedByCategory).map(([catId, items]) => (
                  <div key={catId}>
                    {activeCategory === 'all' && !search && (
                      <h2 className="text-base font-extrabold mb-4 flex items-center gap-2" style={{ color: '#f5f0e8' }}>
                        {(() => {
                          const cat = FAQ_CATEGORIES.find((c) => c.id === catId);
                          const Icon = cat?.icon ?? HelpCircle;
                          return (
                            <>
                              <span className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(212,168,67,0.12)' }}>
                                <Icon size={14} style={{ color: '#d4a843' }} />
                              </span>
                              {categoryLabels[catId] ?? catId}
                            </>
                          );
                        })()}
                      </h2>
                    )}
                    <div className="flex flex-col gap-2">
                      {items.map((item) => (
                        <AccordionItem key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: '#1a1a1a' }}>
                  <Search size={24} style={{ color: '#5a4a35' }} />
                </div>
                <h3 className="text-base font-extrabold mb-2" style={{ color: '#f5f0e8' }}>No results found</h3>
                <p className="text-sm max-w-sm leading-relaxed mb-5" style={{ color: '#8a7a65' }}>
                  We couldn&apos;t find any questions matching &ldquo;{search}&rdquo;. Try a different search term or browse by category.
                </p>
                <button
                  onClick={() => setSearch('')}
                  className="text-sm font-semibold hover:underline"
                  style={{ color: '#d4a843' }}
                >
                  Clear search
                </button>
              </div>
            )}

            {/* Still have questions CTA */}
            <div className="mt-14 rounded-3xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" style={{ background: 'linear-gradient(135deg, #1a1500 0%, #221c00 100%)', border: '1px solid #3a2e00' }}>
              <div>
                <h3 className="text-base font-extrabold mb-1" style={{ color: '#f5f0e8' }}>Still have questions?</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#8a7a65' }}>
                  Can&apos;t find what you&apos;re looking for? Get in touch and we&apos;ll be happy to help.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <Link href="/contact" className="btn-primary text-sm py-2.5 px-6 text-center">
                  Contact Us
                </Link>
                <Link
                  href="/repairs"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-semibold transition-colors"
                  style={{ border: '1px solid #2a2a2a', backgroundColor: '#1a1a1a', color: '#f5f0e8' }}
                >
                  Book a Repair
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
